/**
 * In-SPA token explorer UI — V2 cyber-sovereign glass.
 * Parity with V1 TokenExplorerPanel; full rewrite (no v1 / explorer-package reuse).
 */
import { BrandTokenMark } from "@/components/brand/BrandTokenMark";
import { Button } from "@/components/ui/Button";
import {
	explorerConfigured,
	useTokenExplorerBalance,
	useTokenExplorerMeta,
	useTokenExplorerTxs,
	type ExplorerToken,
} from "@/hooks/useTokenExplorer";
import { formatAmount } from "@/lib/format-amount";
import { resolveIndexCanisterId } from "@/lib/icrc-history";
import { cn } from "@/lib/utils";
import { useState, type ReactNode } from "react";

type Props = {
	token: ExplorerToken;
	title: string;
};

function MetaCard({
	label,
	children,
	className,
}: {
	label: string;
	children: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn("glass-panel p-4", className)}>
			<p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
				{label}
			</p>
			<div className="mt-1.5 min-w-0">{children}</div>
		</div>
	);
}

export function TokenExplorerView({ token, title }: Props) {
	const [principal, setPrincipal] = useState("");
	const [lookup, setLookup] = useState<string | null>(null);
	const configured = explorerConfigured(token);
	const indexConfigured = !!resolveIndexCanisterId(token);
	const meta = useTokenExplorerMeta(token);
	const bal = useTokenExplorerBalance(token, lookup);
	const txs = useTokenExplorerTxs(token, lookup);

	if (!configured) {
		return (
			<div className="glass-panel space-y-2 p-8 text-center text-sm text-muted-foreground">
				<p>
					{token} ledger canister ID is not configured. Set{" "}
					<code className="rounded bg-muted px-1 font-mono text-xs">
						VITE_CANISTER_ID_{token}_LEDGER
					</code>{" "}
					(and{" "}
					<code className="rounded bg-muted px-1 font-mono text-xs">
						VITE_CANISTER_ID_{token}_INDEX
					</code>{" "}
					for history) after deploy.
				</p>
			</div>
		);
	}

	const symbol = meta.data?.symbol ?? token;
	const displayName = meta.data?.name ?? title;

	return (
		<div className="space-y-6">
			{/* Meta grid */}
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
				<MetaCard label="Token">
					<div className="flex items-center gap-2.5">
						<BrandTokenMark token={token} size="md" />
						<p className="min-w-0 font-semibold leading-tight">
							<span className="block truncate">{displayName}</span>
							<span className="font-mono text-xs font-normal text-muted-foreground">
								{symbol}
							</span>
						</p>
					</div>
				</MetaCard>

				<MetaCard label="Total supply">
					<p className="font-mono-amount text-base font-semibold tabular-nums">
						{meta.isLoading
							? "…"
							: meta.isError
								? "Error"
								: meta.data
									? formatAmount(meta.data.supply, {
											decimals: 4,
											minDecimals: 0,
										})
									: "—"}
					</p>
				</MetaCard>

				<MetaCard label="Transfer fee">
					<p className="font-mono-amount text-base font-semibold tabular-nums">
						{meta.isLoading
							? "…"
							: meta.data
								? formatAmount(meta.data.fee, {
										decimals: 8,
										minDecimals: 0,
									})
								: "—"}
					</p>
				</MetaCard>

				<MetaCard label="Ledger">
					<p
						className="truncate font-mono text-xs text-muted-foreground"
						title={meta.data?.ledgerId}
					>
						{meta.data?.ledgerId ?? "—"}
					</p>
					{meta.data?.indexId ? (
						<p
							className="mt-1 truncate font-mono text-[10px] text-muted-foreground/80"
							title={meta.data.indexId}
						>
							index · {meta.data.indexId}
						</p>
					) : (
						<p className="mt-1 text-[10px] text-muted-foreground">
							Index not configured
						</p>
					)}
				</MetaCard>
			</div>

			{meta.isError && (
				<div className="glass-panel border-danger/40 p-4 text-sm text-danger">
					{(meta.error as Error)?.message ?? "Failed to load token metadata"}
				</div>
			)}

			{/* Account lookup */}
			<div className="glass-panel space-y-4 p-4 sm:p-6">
				<div>
					<h3 className="text-sm font-semibold">Account lookup</h3>
					<p className="mt-1 text-xs text-muted-foreground">
						Query default-subaccount balance
						{indexConfigured ? " and recent index transactions" : ""}.
					</p>
				</div>

				<form
					className="flex flex-col gap-2 sm:flex-row"
					onSubmit={(e) => {
						e.preventDefault();
						setLookup(principal.trim() || null);
					}}
				>
					<input
						type="text"
						placeholder="Principal (aaaaa-...)"
						value={principal}
						onChange={(e) => setPrincipal(e.target.value)}
						spellCheck={false}
						autoComplete="off"
						className="min-h-11 flex-1 rounded-xl border border-border/70 bg-background/60 px-3 font-mono text-sm outline-none ring-primary/30 placeholder:text-muted-foreground focus:ring-2"
						aria-label={`${token} principal lookup`}
					/>
					<Button
						type="submit"
						disabled={!principal.trim()}
						className="sm:min-w-[7.5rem]"
					>
						Look up
					</Button>
				</form>

				{lookup && (
					<div className="space-y-5 border-t border-border/40 pt-4">
						<div>
							<p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
								Balance
							</p>
							<p className="mt-1 font-mono-amount text-lg font-semibold tabular-nums">
								{bal.isLoading
									? "…"
									: bal.isError
										? "Error"
										: bal.data != null
											? `${formatAmount(bal.data.amount, {
													decimals: 6,
													minDecimals: 0,
												})} ${symbol}`
											: "—"}
							</p>
							{bal.isError && (
								<p className="mt-1 text-xs text-danger">
									{(bal.error as Error)?.message ?? "Balance query failed"}
								</p>
							)}
							<p
								className="mt-1 truncate font-mono text-[10px] text-muted-foreground"
								title={lookup}
							>
								{lookup}
							</p>
						</div>

						<div>
							<div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
								<h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
									Recent transactions
								</h4>
								{!indexConfigured && (
									<span className="text-[10px] text-muted-foreground">
										Set VITE_CANISTER_ID_{token}_INDEX for history
									</span>
								)}
							</div>

							{!indexConfigured && (
								<p className="text-sm text-muted-foreground">
									Index canister not configured — balance lookup still works.
								</p>
							)}

							{indexConfigured && txs.isLoading && (
								<p className="text-sm text-muted-foreground">Loading…</p>
							)}
							{indexConfigured && txs.isError && (
								<p className="text-sm text-danger">
									{(txs.error as Error)?.message ?? "Failed to load history"}
								</p>
							)}
							{indexConfigured && txs.data && txs.data.length === 0 && (
								<p className="text-sm text-muted-foreground">No transactions</p>
							)}
							{indexConfigured && txs.data && txs.data.length > 0 && (
								<div className="overflow-x-auto rounded-xl border border-border/40">
									<table className="w-full min-w-[28rem] text-left text-xs">
										<thead className="border-b border-border/40 bg-muted/30">
											<tr>
												<th className="px-3 py-2.5 font-medium">Kind</th>
												<th className="px-3 py-2.5 font-medium">Amount</th>
												<th className="px-3 py-2.5 font-medium">
													Counterparty
												</th>
												<th className="px-3 py-2.5 font-medium">Block</th>
											</tr>
										</thead>
										<tbody>
											{txs.data.map((row) => (
												<tr
													key={row.id}
													className="border-t border-border/30 hover:bg-muted/20"
												>
													<td className="px-3 py-2 font-medium capitalize">
														{row.kind.replace(/_/g, " ")}
													</td>
													<td className="px-3 py-2 font-mono-amount tabular-nums">
														{formatAmount(row.amount, {
															decimals: 6,
															minDecimals: 0,
														})}
													</td>
													<td
														className="max-w-[12rem] truncate px-3 py-2 font-mono text-[10px] text-muted-foreground"
														title={row.counterparty ?? undefined}
													>
														{row.counterparty ?? "—"}
													</td>
													<td className="px-3 py-2 font-mono tabular-nums">
														{row.blockIndex.toString()}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
